/**
 * AI Mix Assistant
 * AI-powered mixing suggestions and analysis
 */

import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import {
  Brain,
  Sparkles,
  Wand2,
  Gauge,
  Sliders,
  Volume2,
  Music2,
  Check,
  X,
  Loader2,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  Target,
  Zap,
  Info,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Genre options
const GENRES = [
  { id: 'pop', name: 'Pop', emoji: '🎵' },
  { id: 'rock', name: 'Rock', emoji: '🎸' },
  { id: 'electronic', name: 'Electronic/EDM', emoji: '🎧' },
  { id: 'hiphop', name: 'Hip-Hop', emoji: '🎤' },
  { id: 'jazz', name: 'Jazz', emoji: '🎷' },
  { id: 'classical', name: 'Classical', emoji: '🎻' },
  { id: 'rnb', name: 'R&B/Soul', emoji: '💜' },
  { id: 'metal', name: 'Metal', emoji: '🤘' },
  { id: 'ambient', name: 'Ambient', emoji: '🌙' },
  { id: 'general', name: 'General', emoji: '🎼' },
];

// Reference styles
const STYLES = [
  { id: 'balanced', name: 'Balanced' },
  { id: 'warm', name: 'Warm' },
  { id: 'bright', name: 'Bright' },
  { id: 'punchy', name: 'Punchy' },
  { id: 'vintage', name: 'Vintage' },
  { id: 'modern', name: 'Modern' },
  { id: 'aggressive', name: 'Aggressive' },
  { id: 'natural', name: 'Natural' },
];

// Track suggestion card
const TrackSuggestionCard = ({ suggestion, onApply }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  
  const { track_id, track_name, suggestions } = suggestion;
  const priority = suggestions?.priority || 'medium';
  const priorityColor = {
    high: 'border-red-500/50 bg-red-500/5',
    medium: 'border-amber-500/50 bg-amber-500/5',
    low: 'border-emerald-500/50 bg-emerald-500/5',
  }[priority];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-xl border ${priorityColor} overflow-hidden`}
    >
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-zinc-800/30"
      >
        <div className="flex items-center gap-3">
          <Music2 className="w-5 h-5 text-zinc-400" />
          <span className="font-medium text-white">{track_name}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            priority === 'high' ? 'bg-red-500/20 text-red-400' :
            priority === 'medium' ? 'bg-amber-500/20 text-amber-400' :
            'bg-emerald-500/20 text-emerald-400'
          }`}>
            {priority} priority
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-zinc-500" />
        ) : (
          <ChevronDown className="w-4 h-4 text-zinc-500" />
        )}
      </button>
      
      <AnimatePresence>
        {isExpanded && suggestions && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 space-y-3 border-t border-zinc-800/50">
              {/* EQ Suggestions */}
              {suggestions.eq && (
                <div className="p-3 bg-zinc-800/30 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Sliders className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm font-medium text-white">EQ</span>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-xs">
                    <div className="text-center">
                      <div className="text-zinc-400">Low</div>
                      <div className="text-white font-mono">{suggestions.eq.low > 0 ? '+' : ''}{suggestions.eq.low}dB</div>
                    </div>
                    <div className="text-center">
                      <div className="text-zinc-400">Low-Mid</div>
                      <div className="text-white font-mono">{suggestions.eq.lowMid > 0 ? '+' : ''}{suggestions.eq.lowMid}dB</div>
                    </div>
                    <div className="text-center">
                      <div className="text-zinc-400">High-Mid</div>
                      <div className="text-white font-mono">{suggestions.eq.highMid > 0 ? '+' : ''}{suggestions.eq.highMid}dB</div>
                    </div>
                    <div className="text-center">
                      <div className="text-zinc-400">High</div>
                      <div className="text-white font-mono">{suggestions.eq.high > 0 ? '+' : ''}{suggestions.eq.high}dB</div>
                    </div>
                  </div>
                  {suggestions.eq.reasoning && (
                    <p className="text-xs text-zinc-500 mt-2 italic">{suggestions.eq.reasoning}</p>
                  )}
                </div>
              )}
              
              {/* Compression Suggestions */}
              {suggestions.compression && (
                <div className="p-3 bg-zinc-800/30 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Gauge className="w-4 h-4 text-violet-400" />
                    <span className="text-sm font-medium text-white">Compression</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="text-center">
                      <div className="text-zinc-400">Threshold</div>
                      <div className="text-white font-mono">{suggestions.compression.threshold}dB</div>
                    </div>
                    <div className="text-center">
                      <div className="text-zinc-400">Ratio</div>
                      <div className="text-white font-mono">{suggestions.compression.ratio}:1</div>
                    </div>
                    <div className="text-center">
                      <div className="text-zinc-400">Makeup</div>
                      <div className="text-white font-mono">+{suggestions.compression.makeup}dB</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                    <div className="text-center">
                      <div className="text-zinc-400">Attack</div>
                      <div className="text-white font-mono">{suggestions.compression.attack}ms</div>
                    </div>
                    <div className="text-center">
                      <div className="text-zinc-400">Release</div>
                      <div className="text-white font-mono">{suggestions.compression.release}ms</div>
                    </div>
                  </div>
                  {suggestions.compression.reasoning && (
                    <p className="text-xs text-zinc-500 mt-2 italic">{suggestions.compression.reasoning}</p>
                  )}
                </div>
              )}
              
              {/* Gain & Panning */}
              <div className="flex gap-2">
                {suggestions.gain_adjustment !== undefined && (
                  <div className="flex-1 p-3 bg-zinc-800/30 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Volume2 className="w-4 h-4 text-amber-400" />
                      <span className="text-sm text-white">Gain: {suggestions.gain_adjustment > 0 ? '+' : ''}{suggestions.gain_adjustment}dB</span>
                    </div>
                  </div>
                )}
                {suggestions.panning !== undefined && (
                  <div className="flex-1 p-3 bg-zinc-800/30 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Target className="w-4 h-4 text-cyan-400" />
                      <span className="text-sm text-white">
                        Pan: {suggestions.panning === 0 ? 'Center' : 
                              suggestions.panning < 0 ? `${Math.abs(suggestions.panning * 100)}% L` : 
                              `${suggestions.panning * 100}% R`}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Apply Button */}
              <Button
                onClick={() => onApply?.(track_id, suggestions)}
                className="w-full bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600"
              >
                <Wand2 className="w-4 h-4 mr-2" />
                Apply to Track
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export const AIMixAssistant = ({
  isOpen,
  onClose,
  tracks,
  projectName,
  onApplySuggestion,
  onApplyMastering,
}) => {
  const [mode, setMode] = useState('mix'); // mix, master, gain
  const [genre, setGenre] = useState('general');
  const [style, setStyle] = useState('balanced');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [masteringSuggestions, setMasteringSuggestions] = useState(null);

  // Analyze mix
  const analyzeMix = async () => {
    // Check if tracks exist
    if (!tracks || tracks.length === 0) {
      toast.error('No tracks to analyze. Add some tracks first!');
      return;
    }
    
    setIsAnalyzing(true);
    setSuggestions(null);
    
    try {
      // Prepare track data - ensure all fields are properly set
      const trackData = tracks.map(track => ({
        track_id: String(track.id || `track-${Date.now()}`),
        track_name: String(track.name || 'Untitled Track'),
        track_type: track.type || 'audio',
        duration: Number(track.duration) || 0,
        peak_level: Number(track.peakLevel) || -6,
        rms_level: Number(track.rmsLevel) || -18,
        frequency_data: track.frequencyData || null,
        current_effects: track.effects || null,
      }));
      
      const response = await fetch(`${API_URL}/api/ai-assistant/analyze-mix`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_name: projectName || 'Untitled Project',
          tracks: trackData,
          genre,
          target_loudness: -14,
          reference_style: style,
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('API Error:', errorData);
        throw new Error(errorData.detail || 'Analysis failed');
      }
      
      const data = await response.json();
      setSuggestions(data.suggestions);
      toast.success('Mix analysis complete!');
    } catch (err) {
      console.error('Error analyzing mix:', err);
      toast.error(err.message || 'Failed to analyze mix');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Get mastering suggestions
  const getMasteringSuggestions = async () => {
    setIsAnalyzing(true);
    setMasteringSuggestions(null);
    
    try {
      const response = await fetch(`${API_URL}/api/ai-assistant/quick-master`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_name: projectName || 'Untitled Project',
          genre,
          master_peak_level: -3,
          master_rms_level: -12,
          target_loudness: -14,
          reference_style: style,
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('API Error:', errorData);
        throw new Error(errorData.detail || 'Mastering failed');
      }
      
      const data = await response.json();
      setMasteringSuggestions(data);
      toast.success('Mastering settings ready!');
    } catch (err) {
      console.error('Error getting mastering:', err);
      toast.error(err.message || 'Failed to get mastering suggestions');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Apply suggestion to track
  const handleApplySuggestion = (trackId, trackSuggestions) => {
    onApplySuggestion?.(trackId, {
      eq: trackSuggestions.eq,
      compression: trackSuggestions.compression,
      gain: trackSuggestions.gain_adjustment,
      pan: trackSuggestions.panning,
    });
    toast.success('Suggestions applied to track!');
  };

  // Apply mastering
  const handleApplyMastering = () => {
    if (masteringSuggestions?.mastering_chain) {
      onApplyMastering?.(masteringSuggestions.mastering_chain);
      toast.success('Mastering chain applied!');
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={e => e.stopPropagation()}
          className="w-full max-w-2xl max-h-[85vh] bg-zinc-900 rounded-2xl border border-zinc-700 overflow-hidden flex flex-col"
          data-testid="ai-mix-assistant"
        >
          {/* Header */}
          <div className="px-6 py-4 bg-gradient-to-r from-violet-500/20 via-purple-500/20 to-fuchsia-500/20 border-b border-zinc-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
                  <Brain className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">AI Mix Assistant</h2>
                  <p className="text-sm text-zinc-400">Powered by GPT-5.2</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="text-zinc-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Mode Tabs */}
            <div className="flex gap-2 mt-4">
              {[
                { id: 'mix', label: 'Mix Analysis', icon: Sliders },
                { id: 'master', label: 'AI Mastering', icon: Zap },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setMode(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    mode === tab.id
                      ? 'bg-violet-500/20 text-violet-400'
                      : 'text-zinc-400 hover:text-white'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Settings */}
            <div className="grid grid-cols-2 gap-4">
              {/* Genre Selection */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Genre</label>
                <div className="grid grid-cols-5 gap-1">
                  {GENRES.slice(0, 10).map(g => (
                    <button
                      key={g.id}
                      onClick={() => setGenre(g.id)}
                      className={`p-2 rounded-lg text-center transition-all ${
                        genre === g.id
                          ? 'bg-violet-500/20 border border-violet-500/50'
                          : 'bg-zinc-800 border border-zinc-700 hover:border-zinc-500'
                      }`}
                      title={g.name}
                    >
                      <span className="text-lg">{g.emoji}</span>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-zinc-500 mt-1">Selected: {GENRES.find(g => g.id === genre)?.name}</p>
              </div>
              
              {/* Style Selection */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Reference Style</label>
                <div className="flex flex-wrap gap-1">
                  {STYLES.map(s => (
                    <button
                      key={s.id}
                      onClick={() => setStyle(s.id)}
                      className={`px-3 py-1.5 rounded-lg text-xs transition-all ${
                        style === s.id
                          ? 'bg-violet-500/20 text-violet-400 border border-violet-500/50'
                          : 'bg-zinc-800 text-zinc-400 hover:text-white border border-zinc-700'
                      }`}
                    >
                      {s.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Analyze Button */}
            <Button
              onClick={mode === 'mix' ? analyzeMix : getMasteringSuggestions}
              disabled={isAnalyzing}
              className="w-full bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 h-12"
              data-testid="analyze-btn"
            >
              {isAnalyzing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Brain className="w-5 h-5 mr-2" />
                  {mode === 'mix' ? `Analyze Mix (${tracks?.length || 0} tracks)` : 'Get Mastering Settings'}
                </>
              )}
            </Button>

            {/* Mix Analysis Results */}
            {mode === 'mix' && suggestions && (
              <div className="space-y-4">
                {/* Overall Assessment */}
                {suggestions.overall_assessment && (
                  <div className="p-4 bg-violet-500/10 border border-violet-500/30 rounded-xl">
                    <div className="flex items-start gap-3">
                      <Lightbulb className="w-5 h-5 text-violet-400 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-white">Assessment</h4>
                        <p className="text-sm text-zinc-300 mt-1">{suggestions.overall_assessment}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Track Suggestions */}
                {suggestions.track_suggestions && suggestions.track_suggestions.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="font-medium text-white">Track Suggestions</h4>
                    {suggestions.track_suggestions.map((suggestion, i) => (
                      <TrackSuggestionCard
                        key={suggestion.track_id || i}
                        suggestion={suggestion}
                        onApply={handleApplySuggestion}
                      />
                    ))}
                  </div>
                )}
                
                {/* Mix Tips */}
                {suggestions.mix_tips && suggestions.mix_tips.length > 0 && (
                  <div className="p-4 bg-zinc-800/50 rounded-xl">
                    <h4 className="font-medium text-white mb-2 flex items-center gap-2">
                      <Info className="w-4 h-4 text-amber-400" />
                      Mix Tips
                    </h4>
                    <ul className="space-y-1">
                      {suggestions.mix_tips.map((tip, i) => (
                        <li key={i} className="text-sm text-zinc-400 flex items-start gap-2">
                          <span className="text-amber-400 mt-1">•</span>
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Mastering Results */}
            {mode === 'master' && masteringSuggestions && (
              <div className="space-y-4">
                <div className="p-4 bg-gradient-to-r from-violet-500/10 to-purple-500/10 border border-violet-500/30 rounded-xl">
                  <h4 className="font-medium text-white mb-3">Mastering Chain for {GENRES.find(g => g.id === genre)?.name}</h4>
                  
                  {/* EQ */}
                  {masteringSuggestions.mastering_chain?.eq && (
                    <div className="p-3 bg-zinc-800/50 rounded-lg mb-2">
                      <div className="flex items-center gap-2 mb-2">
                        <Sliders className="w-4 h-4 text-emerald-400" />
                        <span className="text-sm font-medium text-white">Master EQ</span>
                      </div>
                      <div className="grid grid-cols-4 gap-2 text-xs text-center">
                        <div>
                          <div className="text-zinc-400">Low</div>
                          <div className="text-white font-mono">{masteringSuggestions.mastering_chain.eq.low > 0 ? '+' : ''}{masteringSuggestions.mastering_chain.eq.low}dB</div>
                        </div>
                        <div>
                          <div className="text-zinc-400">Low-Mid</div>
                          <div className="text-white font-mono">{masteringSuggestions.mastering_chain.eq.lowMid > 0 ? '+' : ''}{masteringSuggestions.mastering_chain.eq.lowMid}dB</div>
                        </div>
                        <div>
                          <div className="text-zinc-400">High-Mid</div>
                          <div className="text-white font-mono">{masteringSuggestions.mastering_chain.eq.highMid > 0 ? '+' : ''}{masteringSuggestions.mastering_chain.eq.highMid}dB</div>
                        </div>
                        <div>
                          <div className="text-zinc-400">High</div>
                          <div className="text-white font-mono">{masteringSuggestions.mastering_chain.eq.high > 0 ? '+' : ''}{masteringSuggestions.mastering_chain.eq.high}dB</div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Compression */}
                  {masteringSuggestions.mastering_chain?.compression && (
                    <div className="p-3 bg-zinc-800/50 rounded-lg mb-2">
                      <div className="flex items-center gap-2 mb-2">
                        <Gauge className="w-4 h-4 text-violet-400" />
                        <span className="text-sm font-medium text-white">Master Compression</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs text-center">
                        <div>
                          <div className="text-zinc-400">Threshold</div>
                          <div className="text-white font-mono">{masteringSuggestions.mastering_chain.compression.threshold}dB</div>
                        </div>
                        <div>
                          <div className="text-zinc-400">Ratio</div>
                          <div className="text-white font-mono">{masteringSuggestions.mastering_chain.compression.ratio}:1</div>
                        </div>
                        <div>
                          <div className="text-zinc-400">Makeup</div>
                          <div className="text-white font-mono">+{masteringSuggestions.mastering_chain.compression.makeup}dB</div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Limiter */}
                  {masteringSuggestions.mastering_chain?.limiter && (
                    <div className="p-3 bg-zinc-800/50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Zap className="w-4 h-4 text-amber-400" />
                        <span className="text-sm font-medium text-white">Limiter</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs text-center">
                        <div>
                          <div className="text-zinc-400">Ceiling</div>
                          <div className="text-white font-mono">{masteringSuggestions.mastering_chain.limiter.ceiling}dB</div>
                        </div>
                        <div>
                          <div className="text-zinc-400">Release</div>
                          <div className="text-white font-mono">{masteringSuggestions.mastering_chain.limiter.release}ms</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Tips */}
                {masteringSuggestions.tips && (
                  <div className="p-3 bg-zinc-800/30 rounded-lg">
                    <ul className="space-y-1">
                      {masteringSuggestions.tips.map((tip, i) => (
                        <li key={i} className="text-xs text-zinc-400 flex items-start gap-2">
                          <Check className="w-3 h-3 text-emerald-400 mt-0.5" />
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <Button
                  onClick={handleApplyMastering}
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                  data-testid="apply-mastering"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Apply Mastering Chain
                </Button>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AIMixAssistant;
